import { Button, Input, Select, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

import {
  getHotspotType,
  HOTSPOT_TYPE_OPTIONS,
  isTextHotspot,
} from "../utils/tourEditorUtils";
import styles from "./HotspotEditorPanel.module.css";

export default function HotspotEditorPanel({
  hotspot,
  scenes,
  activeSceneId,
  errors,
  onChange,
  onSave,
  onCancel,
}) {
  const targetOptions = scenes
    .filter(scene => scene.id !== activeSceneId)
    .map(scene => ({ value: scene.id, label: scene.name }));

  if (!hotspot) return null;

  const isText = isTextHotspot(hotspot);
  const isHidden = Boolean(hotspot.isHidden);

  const handleToggleHidden = () => {
    onChange({ ...hotspot, isHidden: !isHidden });
  };

  const handleTypeChange = type => {
    const next = { ...hotspot, type };
    if (isTextHotspot(next)) next.targetSceneId = "";
    else next.webUrl = "";
    onChange(next);
  };

  return (
    <aside className={styles.panel} aria-label="Edit hotspot">
      <div className={styles.header}>
        <h3>Edit Hotspot</h3>
        <Tooltip title={isHidden ? "Show hotspot" : "Hide hotspot"}>
          <button
            type="button"
            className={styles.visibilityToggle}
            onClick={handleToggleHidden}
            aria-label={isHidden ? "Show hotspot" : "Hide hotspot"}
          >
            <FontAwesomeIcon icon={isHidden ? faEyeSlash : faEye} />
          </button>
        </Tooltip>
      </div>

      <div className={styles.field}>
        <label htmlFor="hotspot-label">Label (optional)</label>
        <textarea
          id="hotspot-label"
          className={styles.textarea}
          value={hotspot.name ?? ""}
          onChange={event => onChange({ ...hotspot, name: event.target.value })}
          placeholder={"E.g. Living Room\nSecond Line (optional)"}
          rows={3}
        />
        {errors.name && <span className={styles.error}>{errors.name}</span>}
      </div>

      <div className={styles.field}>
        <label htmlFor="hotspot-type">Type</label>
        <Select
          id="hotspot-type"
          value={getHotspotType(hotspot)}
          options={HOTSPOT_TYPE_OPTIONS}
          onChange={handleTypeChange}
          style={{ width: "100%" }}
        />
      </div>

      {isText ? (
        <div className={styles.field}>
          <label htmlFor="hotspot-web-url">Web URL (optional)</label>
          <Input
            id="hotspot-web-url"
            type="url"
            value={hotspot.webUrl ?? ""}
            onChange={event => onChange({ ...hotspot, webUrl: event.target.value })}
            placeholder="https://example.com"
          />
          {errors.webUrl && <span className={styles.error}>{errors.webUrl}</span>}
        </div>
      ) : (
        <div className={styles.field}>
          <label htmlFor="hotspot-target">Target Scene</label>
          <Select
            id="hotspot-target"
            value={hotspot.targetSceneId || undefined}
            placeholder="Select scene..."
            options={targetOptions}
            onChange={value => onChange({ ...hotspot, targetSceneId: value })}
            style={{ width: "100%" }}
          />
          {errors.targetSceneId && (
            <span className={styles.error}>{errors.targetSceneId}</span>
          )}
        </div>
      )}

      <div className={styles.actions}>
        <Button type="primary" onClick={onSave}>
          Save
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </aside>
  );
}
